import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "../pages/CustomButton";
import { ToastContainer } from "react-toastify";

const DivisionsManager = () => {
  const [divisions, setDivisions] = useState([]);
  const [editingDivision, setEditingDivision] = useState(null); 
  const [editingId, setEditingId] = useState(null); 
  const [divisionName, setDivisionName] = useState("");
  const [dateAdded, setDateAdded] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingId, setSavingId] = useState(null); 
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [newDivisionName, setNewDivisionName] = useState("");
  const [newDateFormed, setNewDateFormed] = useState("");
  const [newIsActive, setNewIsActive] = useState(true);
  const addInputRef = useRef(null);

  useEffect(() => {
    fetchDivisions();
  }, []);

  useEffect(() => {
    if (isAddModalOpen) {
      const today = new Date().toISOString().slice(0, 10);
      setNewDateFormed((v) => (v ? v : today));
      setNewIsActive(true);
      setTimeout(() => addInputRef.current?.focus?.(), 50);
    }
  }, [isAddModalOpen]);

  const fetchDivisions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/divisions/division-summary`, {
        headers: getAuthHeaders(),
      });
      setDivisions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching divisions:", err);
      alert("Failed to fetch divisions");
    } finally {
      setLoading(false);
    }
  };

  const normalizeToDateInput = (raw) => {
    if (!raw) return null;
    try {
      const d = new Date(raw);
      if (isNaN(d.getTime())) return null;
      return d.toISOString().slice(0, 10);
    } catch {
      return null;
    }
  };
  const getId = (div) => {
    if (!div) return null;
    return String(div.id ?? div.division_id ?? div.code ?? "");
  };
  const getDivisionName = (div) => div?.name ?? div?.division_name ?? div?.title ?? "";
  const getDivisionDateRaw = (div) =>
    div?.start_date ?? div?.date_formed ?? div?.date_added ?? div?.created_at ?? div?.date ?? null;
  const getDivisionActive = (div) =>
    div?.is_active !== undefined ? !!div.is_active : div?.active !== undefined ? !!div.active : true;
  const startEditing = (div) => {
    if (!div) return;
    const id = getId(div);
    setEditingId(id);
    const copy = { ...(div || {}) };
    setEditingDivision(copy);

    setDivisionName(getDivisionName(copy));

    const normalized = normalizeToDateInput(getDivisionDateRaw(copy)) || new Date().toISOString().slice(0, 10);
    setDateAdded(normalized);

    setIsActive(getDivisionActive(copy));
  };

  const cancelEditing = () => {
    setEditingDivision(null);
    setEditingId(null);
    setDivisionName("");
    setDateAdded("");
    setIsActive(true);
  };

  const handleUpdateDivision = async (id) => {
    if (!divisionName.trim() || !editingDivision) {
      alert("Please enter a division name");
      return;
    }
    setSaving(true);
    setSavingId(id);
    try {
      const url = `${BASE_URL}/api/divisions/${id}`;
      const payload = {
        name: divisionName.trim(),
        date_formed: dateAdded || null,
        start_date: dateAdded ? new Date(dateAdded).toISOString().split("T")[0] : null,
        is_active: !!isActive,
      };
      const response = await axios.put(url, payload, {
        headers: getAuthHeaders(),
      });

      if (response.status === 200 || response.status === 204) {
        await fetchDivisions();
        cancelEditing();
      } else {
        console.warn("Unexpected update response", response);
        alert("Failed to update division");
      }
    } catch (error) {
      console.error("Error updating division:", error);
      alert("Failed to update division");
    } finally {
      setSaving(false);
      setSavingId(null);
    }
  };

  const handleAddDivision = async (e) => {
    e && e.preventDefault();
    if (!newDivisionName.trim()) {
      alert("Please enter a division name");
      return;
    }

    setSaving(true);
    setSavingId("add");
    try {
      const url = `${BASE_URL}/api/divisions/`;
      const payload = {
        name: newDivisionName.trim(),
        date_formed: newDateFormed || null,
        active: !!newIsActive,
      };

      const response = await axios.post(url, payload, {
        headers: getAuthHeaders(),
      });

      if (response.status === 201 || response.status === 200) {
        setNewDivisionName("");
        setNewDateFormed("");
        setNewIsActive(true);
        setAddModalOpen(false);
        await fetchDivisions();
      } else {
        console.warn("Unexpected add response", response);
        alert("Failed to add division");
      }
    } catch (error) {
      console.error("Error adding division:", error);
      alert("Failed to add division");
    } finally {
      setSaving(false);
      setSavingId(null);
    }
  };

  const handleDeleteDivision = async (id) => {
    if (!window.confirm("Are you sure you want to delete this division?")) return;
    setSaving(true);
    setSavingId(id);
    try {
      const url = `${BASE_URL}/api/divisions/${id}`;
      const response = await axios.delete(url, { headers: getAuthHeaders() });

      if (response.status === 200 || response.status === 204) {
        await fetchDivisions();
        if (String(editingId) === String(id)) {
          cancelEditing();
        }
      } else {
        console.warn("Unexpected delete response", response);
        alert("Failed to delete division");
      }
    } catch (error) {
      console.error("Error deleting division:", error);
      alert("Failed to delete division");
    } finally {
      setSaving(false);
      setSavingId(null);
    }
  };

  const safeCount = (item, keys) => {
    for (const k of keys) {
      if (item[k] !== undefined && item[k] !== null) {
        const n = Number(item[k]);
        return isNaN(n) ? 0 : n;
      }
    }
    return 0;
  };

  const totals = {
    products: divisions.reduce((s, it) => s + safeCount(it, ["products_count", "no_of_products", "product_count"]), 0),
    employees: divisions.reduce((s, it) => s + safeCount(it, ["employees_count", "no_of_employees", "employee_count"]), 0),
    doctors: divisions.reduce((s, it) => s + safeCount(it, ["doctors_count", "no_of_doctors", "doctor_count"]), 0),
    chemists: divisions.reduce((s, it) => s + safeCount(it, ["chemists_count", "no_of_chemists", "chemist_count"]), 0),
    stockists: divisions.reduce((s, it) => s + safeCount(it, ["stockists_count", "no_of_stockists", "stockist_count"]), 0),
  };

  return (
    <div className="flex flex-col border border-gray-200">
      <main className="flex-1 p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">
            <span className="text-blue-700"> Division </span>
            <span className="text-blue-500">Management</span>
          </h1>

          <div className="flex gap-2">
            <button
              onClick={() => setAddModalOpen(true)}
              className="bg-blue-500 text-white px-4 py-2 rounded"
            >
              Add Division
            </button>
          </div>
        </div>

        {/* Table */}
         <div className="border rounded shadow  table-wrapper">
        <table className="unlock-dcr-leave-table text-sm ">
          <thead className="bg-blue-500 text-white">
              <tr>
                <th className="border p-2">S.No</th>
                <th className="border p-2">Code</th>
                <th className="border p-2">Division</th>
                <th className="border p-2">Date Added</th>
                <th className="border p-2">Active</th>
                <th className="border p-2">No Of Products</th>
                <th className="border p-2">No Of Employees</th>
                <th className="border p-2">No Of Doctors</th>
                <th className="border p-2">No Of Chemists</th>
                <th className="border p-2">No Of Stockists</th>
                <th className="border p-2">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" className="text-center p-4">Loading...</td>
                </tr>
              ) : divisions.length === 0 ? (
                <tr>
                  <td colSpan="11" className="text-center p-4">No divisions found</td>
                </tr>
              ) : (
                divisions.map((div, index) => {
                  const key = getId(div) || String(index);
                  const isRowEditing = editingId && String(editingId) === String(getId(div));
                  const rawDate = getDivisionDateRaw(div);
                  const dateDisplay = normalizeToDateInput(rawDate) || "-";
                  const code = div.code ?? div.division_id ?? "-";
                  const displayName = getDivisionName(div) || "-";
                  const activeDisplay = getDivisionActive(div) ? "Active" : "Deactive";

                  return (
                    <tr key={key} className={isRowEditing ? "bg-green-50" : ""}>
                      <td className="border p-2 text-center">{index + 1}</td>
                      <td className="border p-2 text-center">{code}</td>

                      <td className="border p-2">
                        {isRowEditing ? (
                          <input
                            type="text"
                            value={divisionName}
                            onChange={(e) => setDivisionName(e.target.value)}
                            className="w-full border px-2 py-1"
                          />
                        ) : (
                          <span className="pl-2">{displayName}</span>
                        )}
                      </td>

                      <td className="border p-2 text-center">
                        {isRowEditing ? (
                          <input
                            type="date"
                            value={dateAdded}
                            onChange={(e) => setDateAdded(e.target.value)}
                            className="border px-2 py-1"
                          />
                        ) : (
                          dateDisplay
                        )}
                      </td>

                      <td className="border p-2 text-center">
                        {isRowEditing ? (
                          <div className="flex items-center gap-3 justify-center">
                            <label className="flex items-center gap-1">
                              <input
                                type="radio"
                                name={`active-${key}`}
                                checked={isActive === true}
                                onChange={() => setIsActive(true)}
                              />
                              <span className="ml-1">Active</span>
                            </label>
                            <label className="flex items-center gap-1">
                              <input
                                type="radio"
                                name={`active-${key}`}
                                checked={isActive === false}
                                onChange={() => setIsActive(false)}
                              />
                              <span className="ml-1">Deactive</span>
                            </label>
                          </div>
                        ) : (
                          activeDisplay
                        )}
                      </td>

                      <td className="border p-2 text-center">
                        {safeCount(div, ["products_count", "no_of_products", "product_count"])}
                      </td>

                      <td className="border p-2 text-center">
                        {safeCount(div, ["employees_count", "no_of_employees", "employee_count"])}
                      </td>

                      <td className="border p-2 text-center">
                        {safeCount(div, ["doctors_count", "no_of_doctors", "doctor_count"])}
                      </td>

                      <td className="border p-2 text-center">
                        {safeCount(div, ["chemists_count", "no_of_chemists", "chemist_count"])}
                      </td>

                      <td className="border p-2 text-center">
                        {safeCount(div, ["stockists_count", "no_of_stockists", "stockist_count"])}
                      </td>

                      <td className="border p-2 text-center">
                        {isRowEditing ? (
                          <>
                            <CustomButton
                              onClick={() => handleUpdateDivision(div.id ?? div.division_id ?? key)}
                              variant="save"
                              size="sm"
                              className="mr-2"
                              disabled={saving && savingId !== (div.id ?? div.division_id ?? key)}
                            >
                              {saving && String(savingId) === String(div.id ?? div.division_id ?? key) ? "Updating..." : "Update"}
                            </CustomButton>
                            <CustomButton
                              onClick={cancelEditing}
                              variant="cancel"
                              size="sm"
                              className="mr-2"
                            >
                              Cancel
                            </CustomButton>
                          </>
                        ) : (
                          <>
                            <CustomButton
                              onClick={() => startEditing(div)}
                              variant="edit"
                              size="sm"
                              className="mr-2"
                              disabled={saving && savingId !== null}
                            >
                              Edit
                            </CustomButton>
                            <CustomButton
                              onClick={() => handleDeleteDivision(div.id ?? div.division_id ?? key)}
                              variant="cancel"
                              size="sm"
                              disabled={saving && savingId !== null && String(savingId) !== String(div.id ?? div.division_id ?? key)}
                            >
                              {saving && String(savingId) === String(div.id ?? div.division_id ?? key) ? "Please wait" : "Delete"}
                            </CustomButton>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>

            {divisions.length > 0 && (
              <tfoot>
                <tr className="bg-blue-700 text-white">
                  <td className="p-2" colSpan={5}></td>
                  <td className="p-2 text-center">{totals.products}</td>
                  <td className="p-2 text-center">{totals.employees}</td>
                  <td className="p-2 text-center">{totals.doctors}</td>
                  <td className="p-2 text-center">{totals.chemists}</td>
                  <td className="p-2 text-center">{totals.stockists}</td>
                  <td className="p-2"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </main>

      {/* Add Division Modal */}
      {isAddModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-auto"
          aria-modal="true"
          role="dialog"
        >
          <div
            className="absolute inset-0 bg-black opacity-40"
            onClick={() => setAddModalOpen(false)}
          />

          <div className="relative bg-white rounded shadow-lg w-full max-w-2xl z-10 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Add Division</h2>
              <CustomButton
                type="button"
                variant="cancel"
                onClick={() => setAddModalOpen(false)}
              >
                Close
              </CustomButton>
            </div>

            <form onSubmit={handleAddDivision} className="grid gap-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium">DIVISION NAME <span className="text-red-600">*</span></label>
                  <input
                    ref={addInputRef}
                    type="text"
                    value={newDivisionName}
                    onChange={(e) => setNewDivisionName(e.target.value)}
                    className="mt-1 block w-full border px-2 py-2"
                    placeholder="Enter division name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium">DATE FORMED</label>
                  <input
                    type="date"
                    value={newDateFormed}
                    onChange={(e) => setNewDateFormed(e.target.value)}
                    className="mt-1 block w-full border px-2 py-2"
                  />
                </div>

                <div className="md:col-span-3">
                  <label className="block text-sm font-medium">STATUS</label>
                  <div className="mt-2 flex items-center gap-6">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="new-active"
                        checked={newIsActive === true}
                        onChange={() => setNewIsActive(true)}
                      />
                      <span>Active</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="new-active"
                        checked={newIsActive === false}
                        onChange={() => setNewIsActive(false)}
                      />
                      <span>De Active</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-center gap-4 mt-4">
                <CustomButton
                  type="submit"
                  variant="save"
                  disabled={saving && savingId === "add"}
                >
                  {saving && savingId === "add" ? "Submitting..." : "Submit"}
                </CustomButton>
                <CustomButton
                  onClick={() => {
                    setAddModalOpen(false);
                    setNewDivisionName("");
                    setNewDateFormed("");
                    setNewIsActive(true);
                  }}
                  variant="cancel"
                >
                  Cancel
                </CustomButton>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default DivisionsManager;
